import { Box, Button, Divider, IconButton, Link, TextField, Typography } from "@mui/material";
import { genericFormProps } from "../signup.types";
import { useCallback, useState } from "react";
import LanguageIcon from '@mui/icons-material/Language';
import DeleteIcon from '@mui/icons-material/Delete';

const WebLinksForm: React.FC<genericFormProps> = (props) => {
  const {
    values,
    handleBlur,
    setFieldValue,
    showSteps
  } = props;

  const [webLink, setWebLink] = useState<string>('');
  const [sitemapLink, setSitemapLink] = useState<string>('');
  const [weblinkErrors, setWeblinkErrors] = useState<string>(null);
  const [sitemapErrors, setSitemapErrors] = useState<string>(null);

  const handleAddLink = () => {
    if (webLink) {
      // validate
      setWeblinkErrors(null);
      const validWebLink = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-z]{2,}([/?].*)?$/;
      if (!validWebLink.test(webLink)) {
        setWeblinkErrors('Please enter valid link to content')
      } else {
        // add to google drive folder list
        let copy = [...values.userWebPage];
        copy.push(
          {
            web_link: webLink,
            disabled: false
          }
        )
        setFieldValue('userWebPage', copy)
        setWebLink('')
      }
    }
  }

  const renderSitemapItem = useCallback((sitemap: any, index: number, onDeleteHandler: () => void) => {
    return (
      <Box key={`sitemap-${index}`} display='flex' justifyContent='space-between' alignItems='center' py={1.5} borderBottom={1} borderColor='divider'>
        <Box display='flex' alignItems='center'>
          <LanguageIcon sx={{ mr: 2, fontSize: '20px' }} />
          <Link href={sitemap.web_link} target="_blank" rel="noreferrer" underline="hover">
            <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>{sitemap.web_link}</Typography>
          </Link>
        </Box>
        <IconButton color='error' size='small' onClick={onDeleteHandler}>
          <DeleteIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>
    );
  }, []);

  const handleAddSitemap = () => {
    if (sitemapLink) {
      setSitemapErrors(null);
      const validURL = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-z]{2,}([/?].*)?$/;
      if (!validURL.test(sitemapLink)) {
        setSitemapErrors('Please enter a valid website link');
      } else {
        let copy = [...values.scrape_webpage];
        // Ensure the URL has http/https prefix
        let formattedLink = sitemapLink;
        if (!formattedLink.startsWith('http')) {
          formattedLink = 'https://' + formattedLink;
        }
        copy.push({
          web_link: formattedLink,
          updatedAt: new Date().toISOString()
        });
        setFieldValue('scrape_webpage', copy);
        setSitemapLink('');
      }
    }
  };



  const renderWebLinkListItem = useCallback(
    (document: any, index: number, onDeleteHandler: () => void) => {
      if (!document.web_link) {
        return (
          <Box
            key={`weblink-${index}`}
            display='none'
          ></Box>
        )
      }

      let modifiedWebLink = document.web_link;
      if (!modifiedWebLink.startsWith('http')) {
        modifiedWebLink = 'https://' + document.web_link;
      }
      return (<Box
        key={`weblink-${index}`}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        columnGap='12px'
        sx={{
          borderBottom: 1,
          borderColor: (theme) => theme.palette.divider,
          py: 1.5,
        }}
      >
        <Box display='flex'>
          <LanguageIcon sx={{ mr: 2, fontSize: '20px' }} />
          <Link
            underline="hover"
            href={`${modifiedWebLink}`}
            rel="noreferrer"
            target="_blank"
          >
            <Typography
              align='left'
              variant='body2'
              sx={{
                wordBreak: 'break-word',
              }}
            >
              {document.web_link}
            </Typography>
          </Link>
        </Box>
        <IconButton
          id={`button-delete-${document.name}-${index}`}
          color='error'
          size='small'
          sx={{ width: '30px', height: '30px' }}
          onClick={onDeleteHandler}
        >
          <DeleteIcon
            sx={{
              color: 'error.light',
              fontSize: '18px',
            }}
          />
        </IconButton>
      </Box>
    )},
    []
  );

  return (
    <>
      <Box
        display='flex'
        flexDirection='column'
        width='100%'
        gap={2}
      >
        <Typography variant='h6' mb={-1.5}>
          {(showSteps ? '2. ': '') + 'Add Web Links'}
        </Typography>
        <Typography variant="body2"
          sx={{
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          Connect your personal websites, portfolios, blogs, and any weblinks to your personal identity that will help train your AI Persona.
        </Typography>
        <TextField
          type='string'
          fullWidth
          name='google_drive_links'
          margin='none'
          label='Add Web Link'
          placeholder="www.website.com"
          value={webLink}
          onBlur={handleBlur}
          onChange={(e) => {
            setWebLink(e.target.value)
          }}
          error={Boolean(weblinkErrors)}
          helperText={weblinkErrors}
          autoComplete='off'
        />
        <Button
          variant="contained"
          size='large'
          fullWidth={false}
          sx={{
            width: 'fit-content',
            boxShadow: (theme) => `${theme.shadows[2]} !important`,
          }}
          onClick={handleAddLink}
          disabled={!webLink}
        >
          Add Web Link
        </Button>
      </Box>
      {
        !!values.userWebPage.some((val) => !!val.web_link) &&
        <Box
          display='flex'
          flexDirection='column'
        >
          <Typography variant='h7' mb={1.5}>
            My Web Links
          </Typography>
          <Divider />
          {
            (values?.userWebPage || []).map((document: any, index: number) =>
              renderWebLinkListItem(document, index, () => {
                let copy = [...(values.userWebPage || [])];
                copy?.splice(index, 1);
                setFieldValue('userWebPage', copy || null);
              })
            )
          }
        </Box>
      }


      {/* Sitemaps Section */}
      <Box display='flex' flexDirection='column' width='100%' gap={2} mt={5}>
        <Typography variant='h6'>Add Sitemaps</Typography>
        <Typography variant="body2" color='text.secondary'>
          Train your AI persona with your entire website’s sitemap.
        </Typography>
        <TextField
          fullWidth
          label='Add Sitemap Link'
          placeholder="https://websitename.com/"
          value={sitemapLink}
          onChange={(e) => setSitemapLink(e.target.value)}
          error={Boolean(sitemapErrors)}
          helperText={sitemapErrors}
          autoComplete='off'
        />
        <Button variant="contained" onClick={handleAddSitemap} disabled={!sitemapLink}>
          Add Site Map
        </Button>
      </Box>

      {!!values.scrape_webpage?.length && (
        <Box mt={3}>
          <Typography variant='subtitle1'>My Sitemaps</Typography>
          <Divider />
          {values.scrape_webpage.map((sitemap: any, i: number) =>
            renderSitemapItem(sitemap, i, () => {
              const copy = [...values.scrape_webpage];
              copy.splice(i, 1);
              setFieldValue('scrape_webpage', copy);
            })
          )}
        </Box>
      )}
    </>
  )
}

export default WebLinksForm;
